import React, { useEffect, useState, useRef } from 'react'
import api from '../../middleware/api'
import { toast } from 'react-toastify'
import Modal from '../../components/Common/Modal/Modal'
import { PencilIcon, TrashIcon, PlusCircleIcon, InformationCircleIcon, ExclamationCircleIcon, EyeIcon, EllipsisVerticalIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { usePopup } from '../../context/PopupContext'
import PopupPreview from '../../components/Common/Popup/PopupPreview'
import PopupForm from '../../components/Admin/PopupForm'
import RadixMenu from '../../components/Common/RadixMenu/RadixMenu'
import PopupActionsMenu from '../../components/Admin/PopupActionsMenu'
import PortalModal from '../../components/Common/Modal/PortalModal'
import { formatDateOnly } from '../../utils/dateFormatter'





const PopupConfigurationPage = () => {
    // Utiliser le contexte PopupContext
    const {
        popups,
        loading: contextLoading,
        error: contextError,
        refreshPopups,
    } = usePopup();

    // Charger les popups depuis le contexte au montage du composant
    useEffect(() => {
        refreshPopups();
    }, [refreshPopups]);


    const [selectedPopup, setSelectedPopup] = useState(null)
    const [isCreating, setIsCreating] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)
    const [confirmDelete, setConfirmDelete] = useState(null)
    const [temporaryContent, setTemporaryContent] = useState('')
    const modalRef = useRef(null)
    const deleteModalRef = useRef(null)

    const [popupForm, setPopupForm] = useState({
        title: '',
        content: '',
        startDate: '',
        endDate: '',
        isActive: false,
        type: 'info',
        routes: []
    })


    // Utiliser les erreurs du contexte
    useEffect(() => {
        if (contextError) {
            setError(contextError);
        }
    }, [contextError]);

    const resetForm = () => {
        setPopupForm({
            title: '',
            content: '',
            startDate: new Date().toISOString().split('T')[0],
            endDate: '',
            isActive: false,
            type: 'info',
            routes: []
        })
    }

    const openModal = (isNew = true, popup = null) => {
        if (isNew) {
            resetForm()
            setIsCreating(true)
            setIsEditing(false)
        } else {
            setPopupForm({
                title: popup.title,
                content: popup.content,
                startDate: popup.startDate ? new Date(popup.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                endDate: popup.endDate ? new Date(popup.endDate).toISOString().split('T')[0] : '',
                isActive: popup.isActive,
                type: popup.type,
                routes: popup.routes
            })
            setSelectedPopup(popup)
            setIsEditing(true)
            setIsCreating(false)
        }
        if (modalRef.current) {
            modalRef.current.showModal()
        }
    }

    const closeModal = () => {
        if (modalRef.current) {
            modalRef.current.hideModal()
        }
        resetForm()
        setIsCreating(false)
        setIsEditing(false)
        setSelectedPopup(null)
    }

    const openDeleteModal = (popup) => {
        setConfirmDelete(popup)
        if (deleteModalRef.current) {
            deleteModalRef.current.showModal()
        }
    }

    const closeDeleteModal = () => {
        if (deleteModalRef.current) {
            deleteModalRef.current.hideModal()
        }
        setConfirmDelete(null)
    }

    // Nouvel état pour la prévisualisation admin
    const [previewPopup, setPreviewPopup] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    // La fonction pour ouvrir la prévisualisation
    const openPreviewModal = (popup) => {
        setPreviewPopup(popup);
        setShowPreview(true);
    };

    // Fermer la prévisualisation
    const closePreviewModal = () => {
        setShowPreview(false);
        setTimeout(() => {
            setPreviewPopup(null);
        }, 300);
    };

    const previewCurrentForm = () => {
        const tempPopup = {
            ...popupForm,
            content: temporaryContent || popupForm.content,
            id: 'preview'
        };
        openPreviewModal(tempPopup);
    };

    const createPopup = async (formData = null) => {
        try {
            setIsLoading(true)
            const finalPopupData = {
                ...(formData || popupForm),
                content: temporaryContent || popupForm.content
            };

            const response = await api.post('/popups', finalPopupData)
            if (response.data.success) {
                toast.success('Popup créé avec succès')
                resetForm()
                closeModal()
                // Rafraîchir les popups avec la fonction du contexte
                refreshPopups()
            } else {
                const message = response.data.message || 'Erreur lors de la création du popup'
                setError(message)
                toast.error(message)
            }
        } catch (error) {
            console.error('Erreur lors de la création du popup :', error)
            toast.error('Erreur lors de la création du popup')
        } finally {
            setIsLoading(false)
        }
    }

    const updatePopup = async (formData = null) => {
        try {
            setIsLoading(true)
            const finalPopupData = {
                ...(formData || popupForm),
                content: temporaryContent || popupForm.content
            };

            const response = await api.put(`/popups/${selectedPopup.id}`, finalPopupData)
            if (response.data.success) {
                toast.success('Popup mis à jour avec succès')
                closeModal()
                // Rafraîchir les popups avec la fonction du contexte
                refreshPopups()
            } else {
                const message = response.data.message || 'Erreur lors de la mise à jour du popup'
                setError(message)
                toast.error(message)
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour du popup :', error)
            toast.error('Erreur lors de la mise à jour du popup')
        } finally {
            setIsLoading(false)
        }
    }

    const deletePopup = async () => {
        try {
            setIsLoading(true)
            const response = await api.delete(`/popups/${confirmDelete.id}`)
            if (response.data.success) {
                toast.success('Popup supprimé avec succès')
                closeDeleteModal()
                // Rafraîchir les popups avec la fonction du contexte
                refreshPopups()
            } else {
                const message = response.data.message || 'Erreur lors de la suppression du popup'
                setError(message)
                toast.error(message)
            }
        } catch (error) {
            console.error('Erreur lors de la suppression du popup :', error)
            toast.error('Erreur lors de la suppression du popup')
        } finally {
            setIsLoading(false)
        }
    }

    const togglePopupStatus = async (popup) => {
        try {
            setIsLoading(true)
            const updatedPopup = { ...popup, isActive: !popup.isActive }
            const response = await api.put(`/popups/${popup.id}`, updatedPopup)
            if (response.data.success) {
                toast.success(`Popup ${updatedPopup.isActive ? 'activé' : 'désactivé'} avec succès`)
                // Rafraîchir les popups avec la fonction du contexte
                refreshPopups()
            } else {
                const message = response.data.message || 'Erreur lors de la mise à jour du popup'
                setError(message)
                toast.error(message)
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour du popup :', error)
            toast.error('Erreur lors de la mise à jour du popup')
        } finally {
            setIsLoading(false)
        }
    }

    const formatDate = (dateString) => {
        return formatDateOnly(dateString);
    }



    // Afficher le chargement initial
    if ((isLoading || contextLoading) && popups.length === 0) {
        return (
            <div className='w-full max-w-screen-xl mx-auto p-6'>
                <h1 className='text-2xl font-bold mb-6'>Gestion des popups</h1>
                <div className='flex justify-center items-center py-12'>
                    <div className='loading loading-spinner loading-lg text-primary'></div>
                </div>
            </div>
        )
    }

    return (
        <div className='w-full max-w-screen-xl mx-auto p-6'>
            <div className='flex justify-between items-center mb-6'>
                <h1 className='text-2xl font-bold'>Gestion des popups</h1>
                <button
                    className='btn btn-primary btn-md gap-2'
                    onClick={() => openModal(true)}
                >
                    <PlusCircleIcon className='w-5 h-5' />
                    Créer un popup
                </button>
            </div>

            {error && (
                <div className='alert alert-error mb-6'>
                    <ExclamationCircleIcon className='w-6 h-6' />
                    <span>{error}</span>
                </div>
            )}

            <div className='bg-base-100 rounded-lg shadow-sm overflow-visible'>
                <div className='overflow-x-visible'>
                    <table className='table table-zebra w-full'>
                        <thead>
                            <tr>
                                <th>Titre</th>
                                <th>Période d'affichage</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {popups.length > 0 ? (
                                popups.map((popup) => (
                                    <tr key={popup.id} className='hover'>
                                        <td className='font-medium'>{popup.title}</td>
                                        <td>
                                            {formatDate(popup.startDate)} - {popup.endDate ? formatDate(popup.endDate) : 'Jamais'}
                                        </td>
                                        <td>
                                            <div className='form-control'>
                                                <label className='cursor-pointer label justify-start gap-2'>
                                                    <input
                                                        type='checkbox'
                                                        className='toggle toggle-primary toggle-sm'
                                                        checked={popup.isActive}
                                                        onChange={() => togglePopupStatus(popup)}
                                                    />
                                                    <span className='label-text'>
                                                        {popup.isActive ? 'Actif' : 'Inactif'}
                                                    </span>
                                                </label>
                                            </div>
                                        </td>
                                        <td>
                                            <PopupActionsMenu
                                                popup={popup}
                                                onEdit={() => openModal(false, popup)}
                                                onPreview={() => openPreviewModal(popup)}
                                                onDelete={() => openDeleteModal(popup)}
                                            />
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan='4' className='text-center py-8'>
                                        <div className='flex flex-col items-center gap-2'>
                                            <InformationCircleIcon className='w-8 h-8 text-base-content/50' />
                                            <p className='text-base-content/70'>Aucun popup disponible</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal pour créer/éditer un popup */}
            <PortalModal id='popup-form' layout={true} ref={modalRef} className='max-w-2xl'>
                <PopupForm
                    popupForm={popupForm}
                    setPopupForm={setPopupForm}
                    temporaryContent={temporaryContent}
                    setTemporaryContent={setTemporaryContent}
                    isLoading={isLoading}
                    isCreating={isCreating}
                    onPreview={previewCurrentForm}
                    onSubmit={isCreating ? createPopup : updatePopup}
                    onCancel={closeModal}
                />
            </PortalModal>

            {/* Modal de confirmation de suppression */}
            <PortalModal id='delete-popup' layout={true} ref={deleteModalRef} className='max-w-md'>
                <h3 className='text-xl font-bold mb-4'>Confirmer la suppression</h3>
                <p>
                    Êtes-vous sûr de vouloir supprimer le popup "{confirmDelete?.title}" ?
                    Cette action est irréversible.
                </p>
                <div className='modal-action mt-6'>
                    <button className='btn btn-ghost' onClick={closeDeleteModal} disabled={isLoading}>
                        Annuler
                    </button>
                    <button
                        className='btn btn-error'
                        onClick={deletePopup}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className='loading loading-spinner loading-sm'></span>
                                Chargement...
                            </>
                        ) : 'Supprimer'}
                    </button>
                </div>
            </PortalModal>



            {/* Ajouter le composant AdminPopupPreview à la fin */}
            {previewPopup && <PopupPreview
                popup={previewPopup}
                isOpen={showPreview}
                onClose={closePreviewModal}
            />}
        </div>
    )
}

export default PopupConfigurationPage